import { Brand } from '@dehaat/kisan-app-bl/models/Product';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import NavigationLoader from '@/components/NavigationLoader';
import BrandImageName from '@/components/products/BrandImageName';
import SearchBox from '@/components/SearchBox';
import Spinner from '@/components/Spinner';
import useDebounce from '@/hooks/useDebounce';
import useVendorBrands from '@/hooks/useVendorBrands';
interface Props {
  // Server-rendered brands, replaced once the current vendor is known
  initialBrands?: Brand[];
  heading: string;
  // Brand links open the pre-book listing instead of the regular one
  isPrebook?: boolean;
}
const BrandsListing = ({
  initialBrands = [],
  heading,
  isPrebook = false
}: Props) => {
  const {
    t
  } = useTranslation('main');
  const {
    brands,
    loadingBrands
  } = useVendorBrands(initialBrands, isPrebook);
  const router = useRouter();
  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredBrands, setFilteredBrands] = useState(brands);
  const debouncedSearch = useDebounce((query: string) => {
    setSearchText(query);
  }, 500);
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredBrands(brands);
    } else {
      const filtered = brands.filter(brand => brand.name.toLowerCase().includes(searchText.toLowerCase()));
      setFilteredBrands(filtered);
    }
  }, [searchText, brands]);
  const handleSearch = (query: string) => {
    debouncedSearch(query);
  };
  useEffect(() => {
    const handleStart = (url: string) => {
      const normalized = url.replace(/^\/(en|hi|mr|or|gu|bn)\//, '/');
      if (normalized.startsWith('/products/')) {
        setIsRouteChanging(true);
      }
    };
    const handleComplete = () => setIsRouteChanging(false);
    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);
    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router.events]);
  return <main className="min-h-screen bg-neutral-10" data-sentry-component="BrandsListing" data-sentry-source-file="BrandsListing.tsx">
      <HeaderWithBackButton heading={heading} showCart showHomeIcon={!isPrebook} data-sentry-element="HeaderWithBackButton" data-sentry-source-file="BrandsListing.tsx" />
      {isRouteChanging && <NavigationLoader />}
      {loadingBrands && brands.length === 0 && <Spinner />}
      <section className="bg-white min-h-screen max-w-7xl m-auto px-4 py-20 w-full">
        <SearchBox placeholder={t('search_brands') || 'Search brands...'} onSearch={handleSearch} lang={router.locale || 'en'} className="mb-6" data-sentry-element="SearchBox" data-sentry-source-file="BrandsListing.tsx" />
        <div className={filteredBrands.length > 0 ? 'grid grid-cols-3 md:grid-cols-6 gap-y-8' : 'text-center'}>
          {filteredBrands.length > 0 ? filteredBrands.map(brand => <div key={brand.id}>
                <BrandImageName brand={brand} className="m-auto" isPrebook={isPrebook} />
              </div>) : searchText.trim() !== '' ? <span className="col-span-full py-10">
              {t('no_brands_found') || `No brands found for "${searchText}"`}
            </span> : loadingBrands ? null : <span>{t('no_brands')}</span>}
        </div>
      </section>
    </main>;
};
export default BrandsListing;